import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, Badge } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LogEntry } from "./LogController";
import { HubMessageNotificationType } from "Infrastructure/Models/Messages/MessageNotification";

interface LogWindowProps {
	logs: LogEntry[];
	onClearLogs: () => void;
}

const LogWindow: React.FC<LogWindowProps> = ({ logs, onClearLogs }) => {
	const logsEndRef = useRef<HTMLDivElement | null>(null);
	const [isUserScrolling, setIsUserScrolling] = useState(false);

	useEffect(() => {
		if (!isUserScrolling) {
			logsEndRef.current?.scrollIntoView({ behavior: "instant" });
		}
	}, [isUserScrolling]);

	const handleScroll = (event: React.UIEvent<HTMLElement>) => {
		const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
		if (scrollTop + clientHeight < scrollHeight) {
			setIsUserScrolling(true);
		} else {
			setIsUserScrolling(false);
		}
	};

	return (
		<Box
			sx={{
				position: "fixed",
				bottom: 80,
				right: 16,
				width: "40vw",
				height: "31vh",
				backgroundColor: "#ffffff",
				borderRadius: "8px",
				zIndex: 1000,
				boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "8px 16px",
					backgroundColor: "#f5f5f5",
					borderTopLeftRadius: "8px",
					borderTopRightRadius: "8px",
				}}
			>
				<Typography variant="h6" sx={{ color: "#333" }}>
					Logy
				</Typography>
				<IconButton onClick={onClearLogs} sx={{ color: "#333" }}>
					<Badge
						badgeContent={logs.length}
						color="error"
						sx={{
							"& .MuiBadge-dot": {
								height: "10px",
								width: "10px",
								borderRadius: "50%",
							},
						}}
					>
						<DeleteIcon />
					</Badge>
				</IconButton>
			</Box>

			<Box
				sx={{
					flex: 1,
					backgroundColor: "#1e1e1e",
					color: "#ffffff",
					padding: "8px",
					overflowY: "auto",
					fontFamily: `"Consolas", "Courier New", "Courier", monospace`,
				}}
				onScroll={handleScroll} // Add onScroll event
			>
				{logs.length > 0 ? (
					logs.map((log, _i) => (
						<Typography
							key={log.timestamp}
							sx={{
								color: "#ffffff",
								marginBottom: "2px",
								wordBreak: "break-word",
								fontSize: "0.8rem",
								letterSpacing: "-0.5px",
								fontFamily: `"Consolas", "Courier New", "Courier", monospace`,
								lineHeight: 1.2,
							}}
						>
							<span style={{ color: "#808080" }}>[{log.timestamp}]</span>{" "}
							{!!log.notification && (
								<>
									{!!log.notification.message &&
										!log.notification.requestId && (
											<>
												<span
													style={{
														color:
															log.notification.type ===
															HubMessageNotificationType.Error
																? "#ff7070"
																: "#fff",
													}}
												>
													{log.notification.message}
												</span>
											</>
										)}

									{!!log.notification.requestId && (
										<>
											[{log.notification.requestId?.split("-")[0]}]{" "}
											{log.notification.isReceivedMessage ? "🟢 " : "🔵 "}
											{log.notification.requestMessageType}
											{" ("}
											{log.notification.deviceNames?.join(", ")}
											{")"}
										</>
									)}
								</>
							)}
						</Typography>
					))
				) : (
					<Typography sx={{ color: "#808080" }}>
						Žádné logy k zobrazení
					</Typography>
				)}
				<div ref={logsEndRef} />
			</Box>
		</Box>
	);
};

export default LogWindow;
